import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Accordion from "src/components/Accordion";
import Attachments from "src/components/Attachments";
import List from "src/components/List";
import ListImportFiles from "src/components/ListImportFiles";
import LovDelimiter from "src/components/Lov/Delimiter";
import Modal from "src/components/Modal";
import { ModalCenter } from "src/components/Modal/styles";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { IRefModalImportStructure } from "./interface";

const ModalImportStructure: React.ForwardRefRenderFunction<IRefModalImportStructure> = (_, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
    
	// Modal export delimited structure
	const [importedFiles, setImportedFiles] = useState([]);
	const modalImportStructure = useRef<RCCRefModalProps>(null);
	const [subtitleModal, setSubtitleModal] = useState<string | undefined>(undefined);
	const budgetPlanId = useRef(0);
	const refScrollTo = useRef<HTMLDivElement>(null);
	const { handleSubmit, control, setValue, reset } = useForm();
	const openModalImportStructure  = async (ofa001Id: number, subtitle: string) => {
		reset();
		budgetPlanId.current = ofa001Id;
		setSubtitleModal(subtitle);
		setImportedFiles([]);
		modalImportStructure.current?.openModal();
	};
	const submitImportScructure = async (data: any) => {
		try{
			if(data.dropZone.length === 0){
				addToast({
					color: "yellow",
					description: translate("É necessário ao menos um arquivo para importar"),
					title: translate("Atenção")
				});
				return;
			}
			modalImportStructure.current?.setLoadingButton();
			const formData = new FormData();

			formData.append("Delimiter", data.delimiter);
			formData.append("BudgetPlanId", budgetPlanId.current.toString());

			data.dropZone.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			const response = await RCCServicesApi.postRequest({
				method: "FinancialManagement/IntegrateValues",
				contentType: "multipart/form-data",
				body: formData
			});

			if(response.data.length > 0)
				setImportedFiles(() => response.data);

			refScrollTo.current?.scrollIntoView({
				behavior: "smooth"
			});

			addToast({
				color: "blue",
				description: translate("Arquivos processados. Verifique o status da integração."),
				title: translate("Atenção")
			});
            
			modalImportStructure.current?.resetLoadingButton();
		}catch(err){
			modalImportStructure.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao integrar valores"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalImportStructure
	}));

	return (
		<Modal 
			ref={modalImportStructure}
			header={{
				title: translate("Integrar valores realizados"),
				subtitle: `${translate("Plano orçamentário")}: ${subtitleModal}`,
				icon: {
					name: "upload"
				}
			}}
			styleInline={`${ModalCenter} height: 80vh;`}
			onSubmit={handleSubmit(submitImportScructure)}
		>		
			<Title color="purple" size="md">
				{translate("Instruções")}
			</Title>
			<div style={{ marginLeft: "1rem"}}>
				<Title color="grey">
					{translate("Você pode utilizar arquivos nas extensões (.txt, .csv, .xls, .xlsx ou .ods)")}
				</Title>
				<Title color="grey">
					{translate("Cada arquivo poderá conter no máximo 20Mb de tamanho")}
				</Title>
			</div>
			<Accordion 
				header={<>
					Layouts
				</>}
			>
				<RCCGrid container item direction="column">
					<Accordion 
						header={<>
							{translate("Receita de vendas")}
						</>}
					>
						<List 
							data={[{
								identification: "RE",
								date: "01/2022",
								subsidiaryId: "1",
								salesChannelId: "2",
								productId: "3",
								totalValue: "100",
								quantity: "50"
							}]}
							columns={[{
								idColumn: "identification",
								nameColumn: translate("Identificação"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},{
								idColumn: "subsidiaryId",
								nameColumn: translate("Cód. Unidade/Filial"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "salesChannelId",
								nameColumn: translate("Cód. Canal de vendas"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "productId",
								nameColumn: translate("Cód. Linha de produtos"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "totalValue",
								nameColumn: translate("Valor total"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "quantity",
								nameColumn: translate("Quantidade"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							}]}
							showHeader
							layout="table"
						/>
					</Accordion>

					<Accordion 
						header={<>
							{translate("Dedução de vendas")}
						</>}
					>
						<List 
							data={[{
								identification: "DE",
								date: "01/2022",
								subsidiaryId: "1",
								salesChannelId: "2",
								productId: "3",
								salesDeductionId: "4",
								totalValue: "100"
							}]}
							columns={[{
								idColumn: "identification",
								nameColumn: translate("Identificação"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},{
								idColumn: "subsidiaryId",
								nameColumn: translate("Cód. Unidade/Filial"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "salesChannelId",
								nameColumn: translate("Cód. Canal de vendas"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "productId",
								nameColumn: translate("Cód. Linha de produtos"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "salesDeductionId",
								nameColumn: translate("Cód. Dedução de vendas"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "totalValue",
								nameColumn: translate("Valor total"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							}]}
							showHeader
							layout="table"
						/>
					</Accordion>

					<Accordion 
						header={<>
							{translate("Custos variáveis")}
						</>}
					>
						<List 
							data={[{
								identification: "CV",
								date: "01/2022",
								subsidiaryId: "1",
								productId: "2",
								variableCostsId: "3",
								totalValue: "100"
							}]}
							columns={[{
								idColumn: "identification",
								nameColumn: translate("Identificação"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},{
								idColumn: "subsidiaryId",
								nameColumn: translate("Cód. Unidade/Filial"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "productId",
								nameColumn: translate("Cód. Linha de produtos"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "variableCostsId",
								nameColumn: translate("Cód. Custo variável"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "totalValue",
								nameColumn: translate("Valor total"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							}]}
							showHeader
							layout="table"
						/>
					</Accordion>

					<Accordion 
						header={<>
							{translate("Despesas variáveis")}
						</>}
					>
						<List 
							data={[{
								identification: "DV",
								date: "01/2022",
								subsidiaryId: "1",
								salesChannelId: "2",
								productId: "3",
								variableExpenseId: "4",
								totalValue: "100"
							}]}
							columns={[{
								idColumn: "identification",
								nameColumn: translate("Identificação"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},{
								idColumn: "subsidiaryId",
								nameColumn: translate("Cód. Unidade/Filial"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "salesChannelId",
								nameColumn: translate("Cód. Canal de vendas"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "productId",
								nameColumn: translate("Cód. Linha de produtos"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "variableExpenseId",
								nameColumn: translate("Cód. Despesa variável"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "totalValue",
								nameColumn: translate("Valor total"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							}]}
							showHeader
							layout="table"
						/>
					</Accordion>

					<Accordion 
						header={<>
							{translate("Despesas operacionais")}
						</>}
					>
						<List 
							data={[{
								identification: "DO",
								date: "01/2022",
								subsidiaryId: "1",
								centerCostId: "2",
								accountId: "3",
								totalValue: "100"
							}]}
							columns={[{
								idColumn: "identification",
								nameColumn: translate("Identificação"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},{
								idColumn: "subsidiaryId",
								nameColumn: translate("Cód. Unidade/Filial"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "centerCostId",
								nameColumn: translate("Cód. Centro de custo"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "accountId",
								nameColumn: translate("Cód. Conta"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "totalValue",
								nameColumn: translate("Valor total"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							}]}
							showHeader
							layout="table"
						/>
					</Accordion>

					<Accordion 
						header={<>
							{translate("Outras despesas")}
						</>}
					>
						<List 
							data={[{
								identification: "OU",
								date: "01/2022",
								subsidiaryId: "1",
								centerCostId: "2",
								otherAccountsId: "3",
								totalValue: "100"
							}]}
							columns={[{
								idColumn: "identification",
								nameColumn: translate("Identificação"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},{
								idColumn: "date",
								nameColumn: translate("Data"),
								xs: 2,
								sm: 1,
								truncate: true,
								justify: "left"
							},{
								idColumn: "subsidiaryId",
								nameColumn: translate("Cód. Unidade/Filial"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "centerCostId",
								nameColumn: translate("Cód. Centro de custo"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "otherAccountsId",
								nameColumn: translate("Cód. Outras contas"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							},
							{
								idColumn: "totalValue",
								nameColumn: translate("Valor total"),
								xs: 2,
								sm: 2,
								truncate: true,
								justify: "left"
							}]}
							showHeader
							layout="table"
						/>
					</Accordion>
				</RCCGrid>
			</Accordion>
			
				
			
			<br />
			<RCCGrid container item>
				<RCCGrid container item xs={12} sm={4} md={3} lg={2}>
					<LovDelimiter
						control={control}
						name="delimiter"
					/>
				</RCCGrid>
			</RCCGrid>
			<Attachments 
				name="dropZone"
				setValue={setValue}
				acceptedFormats=".csv,.txt,.xls,.xlsx,.ods"
			/>

			<br />
			<ListImportFiles importedFiles={importedFiles} />
			<div ref={refScrollTo}/>
		</Modal>
	);
};

export default forwardRef(ModalImportStructure);
